import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { UserService } from '@authentication/services/user.service';
import { MerchantSelectors } from '@merchant/store/merchant.slice';

import { DiagnosticEvent } from '../models/diagnostic-event';
import { DiagnosticEventType } from '../models/diagnostic-profile';
import { DiagnosticBufferService } from './diagnostic-buffer.service';
import { TraceContextService } from './trace-context.service';

const LOKI_APPS = [
  'easylot-marketplace',
  'easylot-price-synchronizer',
] as const;

/**
 * Business-context fields only — no PII (no email, no sub, no
 * impersonatedBy). The support engineer can correlate via tracePrefix
 * + merchantId; user identification, if needed, happens via internal
 * tooling against the merchant.
 */
export interface CorrelationData {
  tracePrefix: string;
  traceIds: string[];
  merchantId: string | null;
  siteId: string | null;
  timeWindow: { from: string; to: string } | null;
  lokiQueryHints: string[];
}

@Injectable({ providedIn: 'root' })
export class CorrelationService {
  constructor(
    private readonly buffer: DiagnosticBufferService,
    private readonly traceContext: TraceContextService,
    private readonly userService: UserService,
    private readonly store: Store
  ) {}

  async assemble(): Promise<CorrelationData> {
    const events = this.buffer.snapshot();
    const tracePrefix = this.traceContext.getSessionPrefix();
    const traceIds = this.collectTraceIds(events);
    const timeWindow = this.collectTimeWindow(events);

    const payload = await this.fetchTokenPayload();
    const impMerchantId = stringOrNull(payload['custom:impMerchantId']);
    const ownMerchantId = stringOrNull(payload['custom:merchantId']);
    const merchantId = impMerchantId ?? ownMerchantId;

    const siteId = await this.fetchSiteId();

    const lokiQueryHints = this.buildLokiQueryHints({
      tracePrefix,
      merchantId,
    });

    return {
      tracePrefix,
      traceIds,
      merchantId,
      siteId,
      timeWindow,
      lokiQueryHints,
    };
  }

  private collectTraceIds(events: readonly DiagnosticEvent[]): string[] {
    const ids = new Set<string>();
    for (const ev of events) {
      if (ev.type === DiagnosticEventType.API && ev.payload.traceId) {
        ids.add(ev.payload.traceId);
      }
    }
    return Array.from(ids);
  }

  /**
   * Robust against out-of-order pushes (e.g. an API event whose
   * timestamp was captured at request start but pushed at response
   * time, while a synchronous router event landed in between). Uses
   * min/max instead of head/tail so a `from > to` window cannot arise.
   */
  private collectTimeWindow(
    events: readonly DiagnosticEvent[]
  ): { from: string; to: string } | null {
    if (events.length === 0) {
      return null;
    }
    const timestamps = events.map((e) => e.timestamp);
    return {
      from: new Date(Math.min(...timestamps)).toISOString(),
      to: new Date(Math.max(...timestamps)).toISOString(),
    };
  }

  private async fetchTokenPayload(): Promise<Record<string, unknown>> {
    try {
      const session = await this.userService.getCurrentSessionAsync();
      return (session.getIdToken().payload ?? {}) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private async fetchSiteId(): Promise<string | null> {
    try {
      const id = await firstValueFrom(
        this.store.select(MerchantSelectors.selectSiteId)
      );
      return id ? id : null;
    } catch {
      return null;
    }
  }

  private buildLokiQueryHints(args: {
    tracePrefix: string;
    merchantId: string | null;
  }): string[] {
    const { tracePrefix, merchantId } = args;
    const hints: string[] = [];
    const safePrefix = logqlEscape(tracePrefix);
    const safeMerchant = merchantId ? logqlEscape(merchantId) : null;
    for (const app of LOKI_APPS) {
      hints.push(`{app="${app}"} | json | traceId =~ "${safePrefix}.*"`);
      if (safeMerchant) {
        hints.push(`{app="${app}"} | json | merchantId = "${safeMerchant}"`);
      }
    }
    return hints;
  }
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Escape backslash, double-quote, and newlines so a user-controlled
 * value (today: merchantId — a backend-issued identifier, but defense
 * in depth) cannot break the LogQL string literal a support engineer
 * copy-pastes into Grafana.
 *
 * `\n` and `\r` are converted to their two-character escape sequences
 * (`\\n`, `\\r`), not concatenated with a raw newline character, so the
 * resulting string is a valid LogQL string literal.
 */
function logqlEscape(value: string): string {
  return value.replace(/[\\"\n\r]/g, (c) => {
    if (c === '\n') return '\\n';
    if (c === '\r') return '\\r';
    return '\\' + c;
  });
}
